import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, CurrencyDollarIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { usePonds } from '../../hooks/useApi';
import { retailSaleApi, RetailSale } from '../../utils/retailSalesApi';
import { getErrorMessage } from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import PaymentDepositModal from './PaymentDepositModal';

const RetailSaleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { selectedLocation } = useLocation();
  const { businessType } = useBusinessTypeContext();
  const { data: ponds } = usePonds(selectedLocation?.id || null);
  const [sale, setSale] = useState<RetailSale | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDepositModal, setShowDepositModal] = useState(false);

  // Create a mapping of pond_id to pond name
  const pondNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (ponds) {
      ponds.forEach(pond => {
        map[pond.id] = pond.name;
      });
    }
    return map;
  }, [ponds]);

  useEffect(() => {
    if (id) {
      loadSale();
    }
  }, [id]);

  const loadSale = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError('');
      const data = await retailSaleApi.get(id);
      setSale(data);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to load sale'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; bg: string }> = {
      draft: { color: 'text-gray-700', bg: 'bg-gray-100' },
      completed: { color: 'text-blue-700', bg: 'bg-blue-100' },
      deposit: { color: 'text-green-700', bg: 'bg-green-100' },
      cancelled: { color: 'text-red-700', bg: 'bg-red-100' },
    };
    
    const badge = badges[status] || badges.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.bg} ${badge.color}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getDerivedTotals = (currentSale: RetailSale) => {
    if (currentSale.sale_type !== 'on_farm') {
      return {
        totalAmount: currentSale.total_amount || 0,
        amountReceived: currentSale.amount_received || 0,
        amountDue: currentSale.amount_due || 0,
      };
    }

    const cartonsTotal = (currentSale.cartons || []).reduce((sum, carton) => {
      const qty = Number(carton.quantity_kg) || 0;
      const rate = Number(carton.rate_per_kg) || 0;
      return sum + qty * rate;
    }, 0);

    const totalAmount =
      (currentSale.status === 'draft' || !currentSale.total_amount)
        ? cartonsTotal
        : currentSale.total_amount || cartonsTotal;

    const amountReceived = Number(currentSale.amount_received) || 0;
    const amountDue = totalAmount - amountReceived;

    return { totalAmount, amountReceived, amountDue };
  };

  const getCartonAmount = (carton: { quantity_kg: number; rate_per_kg: number; amount?: number }) => {
    const qty = Number(carton.quantity_kg) || 0;
    const rate = Number(carton.rate_per_kg) || 0;
    const computed = qty * rate;
    if (carton.amount !== undefined && carton.amount !== null && carton.amount > 0) {
      return carton.amount;
    }
    return computed;
  };

  if (businessType !== 'fish') {
    return (
      <div className="container-mobile py-8 text-center">
        <p className="text-gray-600">This page is only available for fish farming locations.</p>
        <Link to="/dashboard" className="text-blue-600 hover:text-blue-800">Go to Dashboard</Link>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container-mobile py-8">
        <div className="flex justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className="container-mobile py-8">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Sale not found'}</p>
          <Link to="/retail-sales/list" className="text-blue-600 hover:text-blue-800">
            Back to Sales List
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/retail-sales/list" className="btn-touch p-2 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{sale.retail_sale_number}</h1>
            <p className="text-gray-600">{selectedLocation?.name}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusBadge(sale.status)}
          {sale.status === 'completed' && (
            <button
              onClick={() => setShowDepositModal(true)}
              className="btn-primary flex items-center space-x-2"
            >
              <CurrencyDollarIcon className="h-5 w-5" />
              <span>Mark Payment Deposited</span>
            </button>
          )}
        </div>
      </div>

      {/* Sale Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sale Information</h2>
          <div className="space-y-3">
            <div>
              <span className="text-sm font-medium text-gray-600">Sale Type:</span>
              <span className="ml-2 text-gray-900 capitalize">
                {sale.sale_type === 'on_farm' ? 'On-Farm' : 'Market'}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Sale Date:</span>
              <span className="ml-2 text-gray-900">
                {new Date(sale.sale_date).toLocaleDateString()}
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Seller:</span>
              <span className="ml-2 text-gray-900">{sale.seller_name || 'N/A'}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Mobile:</span>
              <span className="ml-2 text-gray-900">{sale.seller_mobile || 'N/A'}</span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Ponds:</span>
              <span className="ml-2 text-gray-900">
                {sale.pond_names?.join(', ') || sale.ponds.join(', ')}
              </span>
            </div>
            {sale.income_log_id && (
              <div>
                <span className="text-sm font-medium text-gray-600">Income Log:</span>
                <span className="ml-2 text-green-600 font-medium">Created</span>
              </div>
            )}
          </div>
        </div>

        {/* Amount Info */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Amount Details</h2>
          {sale.sale_type === 'on_farm' ? (
            <div className="space-y-3">
              {(() => {
                const { totalAmount, amountReceived, amountDue } = getDerivedTotals(sale);
                return (
                  <>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Total Amount:</span>
                <span className="text-lg font-bold text-gray-900">
                  ₹{totalAmount.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Amount Received:</span>
                    <span className="text-gray-900">₹{amountReceived.toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-medium text-gray-600">Amount Due:</span>
                    <span className={`text-lg font-bold ${amountDue > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  ₹{amountDue.toFixed(2)}
                </span>
              </div>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="space-y-3">
              {sale.dispatch_quantity_kg !== undefined && sale.dispatch_quantity_kg !== null && (
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-gray-600">Dispatched Quantity:</span>
                  <span className="text-gray-900">{(sale.dispatch_quantity_kg || 0).toFixed(2)} kg</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Sold Quantity:</span>
                <span className="text-gray-900">{(sale.sold_quantity_kg || 0).toFixed(2)} kg</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Returned Quantity:</span>
                <span className="text-gray-900">{(sale.returned_quantity_kg || 0).toFixed(2)} kg</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Rate per kg:</span>
                <span className="text-gray-900">₹{(sale.rate_per_kg || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-medium text-gray-600">Gross Amount:</span>
                <span className="text-gray-900">₹{(sale.gross_amount || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm font-medium text-gray-600">Total Expenses:</span>
                <span className="text-red-600">₹{(sale.total_expenses || 0).toFixed(2)}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="text-sm font-medium text-gray-600">Net Amount:</span>
                <span className="text-lg font-bold text-green-600">
                  ₹{(sale.net_amount || 0).toFixed(2)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Fish Crates (On-Farm) */}
        {sale.sale_type === 'on_farm' && sale.cartons && sale.cartons.length > 0 && (
          <div className="card p-6 md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Fish Crates</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Crate #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pond</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Species</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Weight (kg)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate (₹/kg)</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sale.cartons.map((carton, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">{carton.carton_number || index + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{pondNameMap[carton.pond_id] || carton.pond_id}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{carton.species || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{Number(carton.quantity_kg || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">₹{Number(carton.rate_per_kg || 0).toFixed(2)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">₹{getCartonAmount(carton).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Species Lines (Market) */}
        {sale.sale_type === 'market' && sale.species_lines && sale.species_lines.length > 0 && (
          <div className="card p-6 md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Species Summary</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm border border-gray-300 rounded-lg">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700 border-r border-gray-300">Species</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Dispatched (kg)</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Sold (kg)</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Returned (kg)</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700 border-r border-gray-300">Rate (₹/kg)</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount (₹)</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sale.species_lines.map((line, idx) => {
                    const dispatched = Number(line.dispatched_quantity_kg) || 0;
                    const sold = Number(line.sold_quantity_kg) || 0;
                    const returned = Number(line.returned_quantity_kg) || 0;
                    const rate = Number(line.rate_per_kg) || 0;
                    const amount = sold * rate;
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900 border-r border-gray-200">{line.species || `Species ${idx + 1}`}</td>
                        <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{dispatched.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{sold.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{returned.toFixed(2)}</td>
                        <td className="px-4 py-3 text-center text-gray-700 border-r border-gray-200">{rate.toFixed(2)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{amount.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-gray-50 font-semibold border-t-2 border-gray-400">
                    <td className="px-4 py-3 text-gray-900 border-r border-gray-300">Total</td>
                    <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">
                      {sale.species_lines.reduce((sum, line) => sum + (Number(line.dispatched_quantity_kg) || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">
                      {sale.species_lines.reduce((sum, line) => sum + (Number(line.sold_quantity_kg) || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">
                      {sale.species_lines.reduce((sum, line) => sum + (Number(line.returned_quantity_kg) || 0), 0).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-center text-gray-900 border-r border-gray-300">-</td>
                    <td className="px-4 py-3 text-right text-gray-900">₹{(sale.gross_amount || 0).toFixed(2)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Expenses (Market) */}
        {sale.sale_type === 'market' && sale.expenses && sale.expenses.length > 0 && (
          <div className="card p-6 md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Market Expenses</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sale.expenses.map((expense, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 text-sm text-gray-900">{expense.description}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-gray-900">₹{expense.amount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">Total Expenses</td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">
                      ₹{(sale.total_expenses || 0).toFixed(2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        )}

        {/* Payment Deposit Info */}
        {sale.status === 'deposit' && (
          <div className="card p-6 md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Payment Deposit</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-gray-600">Payment Method:</span>
                <span className="ml-2 text-gray-900 capitalize">{sale.payment_method || 'N/A'}</span>
              </div>
              {sale.deposit_date && (
                <div>
                  <span className="text-sm font-medium text-gray-600">Deposit Date:</span>
                  <span className="ml-2 text-gray-900">
                    {new Date(sale.deposit_date).toLocaleDateString()}
                  </span>
                </div>
              )}
              {sale.deposit_slip_image && (
                <div className="md:col-span-2">
                  <span className="text-sm font-medium text-gray-600 block mb-2">Deposit Slip:</span>
                  <img
                    src={sale.deposit_slip_image}
                    alt="Deposit slip"
                    className="max-w-md h-64 object-contain border border-gray-200 rounded-lg bg-gray-50 cursor-pointer"
                    onClick={() => window.open(sale.deposit_slip_image, '_blank')}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Remarks */}
        {sale.remarks && (
          <div className="card p-6 md:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Remarks</h2>
            <p className="text-gray-600">{sale.remarks}</p>
          </div>
        )}
      </div>

      {/* Payment Deposit Modal */}
      {showDepositModal && sale && (
        <PaymentDepositModal
          sale={sale}
          isOpen={showDepositModal}
          onClose={() => setShowDepositModal(false)}
          onSuccess={() => {
            loadSale();
            setShowDepositModal(false);
          }}
        />
      )}
    </div>
  );
};

export default RetailSaleDetail;

